/**
 * Resolves the portal landing route and nav hrefs from the org's
 * `portal_member_experience` config, cached in the `curaition_user`
 * localStorage blob by the login bootstrap (`/auth/me`).
 *
 * Absent or malformed config always degrades to today's behaviour
 * (`/portal/dashboard`, full nav) — a bad config must never strand a user.
 */

import { useStoredUser } from '@/services/auth/stored-user';
import { usePortalPermissions } from '@/hooks/auth/usePortalPermissions';
import type { PortalMemberExperience } from '../../../../shared/types/api';

export type { PortalMemberExperience };

export const PORTAL_HOME_FALLBACK = '/portal/dashboard';

/** Canonical (RFC 4122-ish, case-insensitive) UUID shape. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Display label per nav key. Shared with `PortalSidebar` so the sidebar entry
 * and any "Back to {section}" affordance name the same destination the same
 * way — a member whose Roster link says "Roster" must not meet a breadcrumb
 * calling it "Projects" (CUR-1395).
 */
export const PORTAL_NAV_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  sources: 'Sources',
  projects: 'Projects',
  content: 'Content',
  patterns: 'Patterns',
  entities: 'Entities',
  brand_safety: 'Brand Safety',
  flag_digests: 'Flag Digests',
  team: 'Team',
  usage: 'Usage',
  billing: 'Billing',
  settings: 'Settings',
  roster: 'Roster',
};

/** Nav keys whose href is static. `roster` is config-derived, so it is absent. */
export const PORTAL_NAV_KEYS: Record<string, string> = {
  dashboard: '/portal/dashboard',
  sources: '/portal/sources',
  projects: '/portal/projects',
  content: '/portal/content',
  patterns: '/portal/patterns',
  entities: '/portal/entities',
  brand_safety: '/portal/sentinel/flags',
  flag_digests: '/portal/sentinel/digests',
  team: '/portal/team',
  usage: '/portal/usage',
  billing: '/portal/billing',
  settings: '/portal/settings',
};

/** True when the blob is shaped well enough to act on. */
export function isValidExperience(value: unknown): value is PortalMemberExperience {
  if (!value || typeof value !== 'object') return false;
  const nav = (value as PortalMemberExperience).nav;
  return Array.isArray(nav) && nav.every((k) => typeof k === 'string');
}

export function resolvePortalNavHref(
  key: string,
  config: PortalMemberExperience | null,
): string | null {
  if (key === 'roster') {
    const id = config?.roster_project_id;
    // A malformed id (paste error, wrong shape, stray path segment) must not
    // build a bad href — return null so the caller's fallback chain takes
    // over instead of navigating into an unmatched route.
    if (!id || !UUID_RE.test(id)) return null;
    return `/portal/projects/${id}`;
  }
  return PORTAL_NAV_KEYS[key] ?? null;
}

/**
 * The landing rule, as a pure function. Callers that resolve outside React
 * state (CallbackPage, AcceptInvitePage — both navigate in the same tick as
 * their localStorage write, where a hook value would be stale) use this
 * directly, so the rule has exactly one implementation.
 *
 * `isOrgAdmin` mirrors the bypass PortalSidebar already applies to nav
 * restriction: an org admin/owner always gets the full nav, so they must
 * also always land on the dashboard rather than a member-only roster route.
 * Callers outside React state pass the flag in directly (see call sites in
 * CallbackPage / AcceptInvitePage) rather than this function reaching for a
 * hook itself — it stays a pure function either way.
 */
export function resolvePortalHome(
  config: PortalMemberExperience | null | undefined,
  isOrgAdmin = false,
): string {
  if (isOrgAdmin) return PORTAL_HOME_FALLBACK;
  if (!isValidExperience(config)) return PORTAL_HOME_FALLBACK;

  const { landing, nav } = config;
  if (landing && nav.includes(landing)) {
    const href = resolvePortalNavHref(landing, config);
    if (href) return href;
  }

  // The dashboard is the wrong fallback for a restricted member: it is the one
  // page their nav does not carry, so landing there strands them with no link
  // back. Prefer the first nav entry that resolves, and only fall through to
  // the dashboard when the config resolves to nothing at all — which is also
  // the case where PortalSidebar degrades to the full nav (CUR-1395).
  for (const key of nav) {
    const href = resolvePortalNavHref(key, config);
    if (href) return href;
  }

  return PORTAL_HOME_FALLBACK;
}

/** Reactive wrapper: the org's configured member landing path. */
export function usePortalHome(): string {
  const { isOrgAdmin } = usePortalPermissions();
  return resolvePortalHome(useStoredUser()?.portalMemberExperience, isOrgAdmin);
}

/**
 * Sections a restricted member reaches only by drilling down, never from the
 * nav. Allowed as CHILD paths (`/portal/content/<id>`), never as the section
 * index itself — the roster links straight into content and pattern detail
 * pages, so blocking these would strand the member on their own home page.
 */
const PORTAL_DETAIL_PREFIXES: readonly string[] = [
  '/portal/content/',
  '/portal/patterns/',
  '/portal/entities/',
  '/portal/creators/',
  '/portal/sources/',
];

/**
 * Always reachable regardless of nav config — chrome, not sections. Matched
 * as subtrees, so `/portal` itself is deliberately NOT here: it would make
 * every path beneath it allowed and turn the whole rule into a no-op.
 */
const PORTAL_ALWAYS_ALLOWED: readonly string[] = ['/portal/profile', '/portal/onboarding'];

function matchesSection(path: string, prefix: string): boolean {
  return path === prefix || path.startsWith(prefix + '/');
}

/**
 * Is `path` inside the surface this member's org configured for them?
 *
 * Restricting the sidebar alone only hides links — every page stayed
 * reachable by URL, by browser history, and by any breadcrumb whose target
 * was resolved from history. That is how a Gymshark member landed on the
 * Projects list from their roster's "Back to projects" link and then cycled
 * between two pages their nav never offered (CUR-1395).
 *
 * Deny-by-default over the portal tree, with three escapes: the member's own
 * nav destinations, drill-down detail routes, and portal chrome. Non-portal
 * paths are not this rule's business — `AdminGuard` owns those.
 *
 * Every caller redirects a denied path to `resolvePortalHome`, so a wrong
 * answer here costs a bounce home, never a lockout. The empty-allowlist
 * degrade mirrors `PortalSidebar`: a config that resolves to nothing gets the
 * full portal rather than a redirect loop against its own landing page.
 */
export function isPortalPathAllowed(
  path: string,
  config: PortalMemberExperience | null | undefined,
  isOrgAdmin = false,
): boolean {
  if (isOrgAdmin || !isValidExperience(config)) return true;

  const clean = (path.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/portal');
  if (!matchesSection(clean, '/portal')) return true;
  // `/portal` exactly is the index route; its own redirect resolves home.
  if (clean === '/portal') return true;

  const allowed = config.nav
    .map((key) => resolvePortalNavHref(key, config))
    .filter((href): href is string => href !== null);
  if (allowed.length === 0) return true;

  if (allowed.some((href) => matchesSection(clean, href))) return true;
  if (PORTAL_ALWAYS_ALLOWED.some((href) => matchesSection(clean, href))) return true;
  return PORTAL_DETAIL_PREFIXES.some(
    (prefix) => clean.startsWith(prefix) && clean.length > prefix.length,
  );
}

/**
 * Label for an allowed nav destination, e.g. `/portal/projects/<roster-id>`
 * → "Roster". Returns null when the path is not one of this member's nav
 * destinations, so callers can fall back to the generic route registry.
 */
export function resolvePortalNavLabel(
  path: string,
  config: PortalMemberExperience | null | undefined,
): string | null {
  if (!isValidExperience(config)) return null;
  const clean = path.split('?')[0].split('#')[0].replace(/\/+$/, '');
  for (const key of config.nav) {
    const href = resolvePortalNavHref(key, config);
    if (href && matchesSection(clean, href)) return PORTAL_NAV_LABELS[key] ?? null;
  }
  return null;
}

/** Reactive wrapper: is the given path inside this member's configured surface? */
export function usePortalPathAllowed(path: string): boolean {
  const { isOrgAdmin } = usePortalPermissions();
  return isPortalPathAllowed(path, useStoredUser()?.portalMemberExperience, isOrgAdmin);
}
